import axios from 'axios';
import { api } from './client';
import type { Envelope } from './types';

const BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export interface RefreshRes {
  accessToken: string;
}

export interface OtpRequestRes {
  requestId: string;
  expiresInSec: number;
}

export interface LoginRes {
  accessToken: string;
  accessExpiresInSec: number;
  user: {
    id: string;
    nickname: string;
    provider: string;
  };
}

export interface MeRes {
  id: string;
  nickname: string;
  provider: string;
  createdAt: string;
}

export async function requestOtp(email: string) {
  const { data } = await api.post<Envelope<OtpRequestRes>>('/auth/otp/request', { email });
  return data.data;
}

export async function verifyOtp(email: string, code: string, nickname?: string) {
  const { data } = await api.post<Envelope<LoginRes>>('/auth/otp/verify', { email, code, nickname });
  return data.data;
}

export async function fetchMe() {
  const { data } = await api.get<Envelope<MeRes>>('/auth/me');
  return data.data;
}

export async function logout() {
  await api.post('/auth/logout');
}

/** 회원 탈퇴 — 서버에서 소프트 삭제 + 익명화 후 refresh 토큰 폐기. */
export async function deleteAccount() {
  await api.delete('/auth/me');
}

/**
 * Preemptively refresh the access token using the refresh cookie.
 * Raw axios call (not `api`) so we skip the request interceptor's
 * Authorization header and the response interceptor's retry loop.
 * In-flight dedup: refresh 토큰은 1회용(rotation)이라 동시 호출(StrictMode
 * 이중 마운트 등)이 같은 쿠키를 재사용하면 재사용 감지에 걸린다.
 */
let inflightRefresh: Promise<string> | null = null;

export async function refreshAccess(): Promise<string> {
  if (!inflightRefresh) {
    inflightRefresh = axios
      .post<Envelope<RefreshRes>>(`${BASE_URL}/auth/refresh`, null, {
        withCredentials: true,
      })
      .then(({ data }) => data.data.accessToken)
      .finally(() => {
        inflightRefresh = null;
      });
  }
  return inflightRefresh;
}
